/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Component, Element, Event, EventEmitter, h, Prop } from "@stencil/core";
import { VideoplayerLocalization, VideoplayerProgressEventArgs } from "../constants/videoplayer-types";
import { formatTime } from "../../../utils/date-helper";
import { stringFormat } from "../../../utils/string-format";
import pfx from "../../../utils/style-class-prefix-helper";
import { StyledComponent } from "../../../decorators/styled-component";
import { KeyboardEvent } from "react";
import { KeyIds } from "../../../constants/keys";
import { uniqueId } from "../../../utils/unique-id";
// import { InitLogging } from "../../../decorators/init-logging";

@Component({
  tag: "eon-ui-videoplayer-controls",
  styleUrl: "controls.scss",
  shadow: false
})
export class VideoplayerControls {
  // @InitLogging() sentryTransaction;
  private progressDescriptionId: string = uniqueId();

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiVideoplayerControlsElement;

  /**
   *
   */
  @Event() fullscreenToggleEvent: EventEmitter<void>;

  /**
   *
   */
  @Event() muteToggleEvent: EventEmitter<void>;

  /**
   *
   */
  @Event() playToggleEvent: EventEmitter<void>;

  /**
   *
   */
  @Event() seekEvent: EventEmitter<VideoplayerProgressEventArgs>;

  /**
   *
   */
  @Event() keyboardSeekEvent: EventEmitter<KeyboardEvent>;

  /**
   *
   */
  @Event() toggleCaptionsEvent: EventEmitter<void>;

  /**
   *
   */
  @Prop() duration: number;

  /**
   *
   */
  @Prop() isFullscreen: boolean;

  /**
   *
   */
  @Prop() isMuted: boolean;

  /**
   *
   */
  @Prop() isPlaying: boolean;

  /**
   *
   */
  @Prop() percentPlayed: number;

  /**
   *
   */
  @Prop() secondsPlayed: number;

  /**
   *
   */
  @Prop() hideMuteButton: boolean;

  /**
   *
   */
  @Prop() captions: boolean;

  /**
   *
   */
  @Prop() hideCaptionsButton: boolean;

  /**
   *
   */
  @Prop() currentLocalization: VideoplayerLocalization;

  /**
   *
   */
  @Prop() hideFullscreenButton: boolean;

  handleSeek(e: MouseEvent): void {
    const { clientX, currentTarget } = e;
    const { left, width } = (currentTarget as Element).getBoundingClientRect();
    const seekPercent = ((clientX - left) / width) * 100;
    const seekSecond = (seekPercent / 100) * this.duration;

    this.seekEvent.emit({ percent: seekPercent, seconds: seekSecond });
  }

  handleProgressKeyDown(event: KeyboardEvent): void {
    if (event.key === KeyIds.RIGHT || event.key === KeyIds.LEFT) {
      this.keyboardSeekEvent.emit(event);
    }

    if (event.key === KeyIds.SPACE || event.key === KeyIds.ENTER) {
      this.playToggleEvent.emit();
    }
  }

  render() {
    const percentPlayedLabel = stringFormat(this.currentLocalization?.percentPlayed, String(this.percentPlayed));

    return (
      <div
        class={pfx({
          controls: true,
          fullscreen: this.isFullscreen
        })}
      >
        {/* Progress bar */}
        <div class={pfx("progress-bar")}>
          <div class={pfx("progress-bar-description")} id={this.progressDescriptionId}>
            {this.currentLocalization?.videoButtonDescription}
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <progress
            max={100}
            onClick={(e) => this.handleSeek(e)}
            value={this.percentPlayed || 0}
            /* eslint-disable-next-line */
            tabIndex={0}
            onKeyDown={(e) => this.handleProgressKeyDown(e as any)}
            aria-describedby={this.progressDescriptionId}
          >
            {/* for ScreenReaders: */}
            {percentPlayedLabel}
          </progress>
        </div>

        <div class={pfx("playback")}>
          {/* Play/Pause button */}
          <button
            class={pfx("button play")}
            title={this.currentLocalization?.togglePlayPause}
            type="button"
            onClick={(_e) => {
              if (_e.x === 0 && _e.y === 0) return;
              this.playToggleEvent.emit();
            }}
            onKeyDown={(e) => {
              // if the key is being held down such that it is automatically repeating we don't want to repeat playToggleEvent.
              if (e.repeat) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }

              if (e.code === "Enter" || e.code === "Space") {
                e.stopPropagation();
                e.preventDefault();
                this.playToggleEvent.emit();
              }
            }}
            aria-pressed={this.isPlaying ? "true" : "false"}
            aria-label={this.currentLocalization?.togglePlayPause}
          >
            <eon-ui-icon
              noSpace={false}
              name={this.isPlaying ? "pause" : "play"}
              title={this.currentLocalization?.togglePlayPause}
              size="small"
              aria-hidden="true"
            ></eon-ui-icon>
          </button>

          {/* Progress bar time*/}
          <span class={pfx("progress-bar-time")}>
            {`${formatTime(this.secondsPlayed || 0)} / ${formatTime(this.duration || 0)}`}
          </span>

          {/* Captions button */}
          {!this.hideCaptionsButton && (
            <button
              type="button"
              class={pfx("button captions")}
              onClick={(_e) => this.toggleCaptionsEvent.emit()}
              title={this.currentLocalization?.toggleCaptions}
              aria-label={this.currentLocalization?.toggleCaptions}
              aria-pressed={this.captions ? "true" : "false"}
            >
              <eon-ui-icon
                no-space="false"
                name={this.captions ? "captions-on" : "captions-off"}
                title={this.currentLocalization?.toggleCaptions}
                size="small"
                aria-hidden="true"
              ></eon-ui-icon>
            </button>
          )}

          {/* Mute/Unmute button */}
          {!this.hideMuteButton && (
            <button
              type="button"
              class={pfx("button mute")}
              onClick={(_e) => this.muteToggleEvent.emit()}
              title={this.currentLocalization?.toggleMute}
              aria-pressed={this.isMuted ? "true" : "false"}
              aria-label={this.currentLocalization?.toggleMute}
            >
              <eon-ui-icon
                noSpace={false}
                name={!this.isMuted ? "volume_normal" : "volume_mute"}
                title={this.currentLocalization?.toggleMute}
                size="small"
                aria-hidden="true"
              ></eon-ui-icon>
            </button>
          )}
          {/* Toggle Fullscreen button */}
          {!this.hideFullscreenButton && (
            <button
              type="button"
              class={pfx("button fullscreen")}
              onClick={(_e) => this.fullscreenToggleEvent.emit()}
              title={this.currentLocalization?.toggleFullscreen}
              aria-pressed={this.isFullscreen ? "true" : "false"}
              aria-label={this.currentLocalization?.toggleFullscreen}
            >
              <eon-ui-icon
                noSpace={false}
                title={this.currentLocalization?.toggleFullscreen}
                name={this.isFullscreen ? "fullscreen_exit" : "fullscreen_enter"}
                size="small"
                aria-hidden="true"
              ></eon-ui-icon>
            </button>
          )}
        </div>
      </div>
    );
  }
}
